import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { useState, useEffect } from "react";
import { formatDate } from "@/utils/dateFormatters";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { safeParseDate } from "@/utils/dateValidation";

interface TenderCalendarProps {
  tenders: any[];
}

// Helper type guard function to check if a value is a record/object
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export const TenderCalendar = ({ tenders }: TenderCalendarProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [selectedDateItems, setSelectedDateItems] = useState<{tenders: any[], tasks: any[]}>({
    tenders: [],
    tasks: []
  });

  // Fetch tasks for all tenders
  const { data: tasks = [] } = useQuery({
    queryKey: ['tender-tasks', tenders.map(t => t.id).join(',')],
    queryFn: async () => {
      if (tenders.length === 0) return [];
      
      const { data, error } = await supabase
        .from('tender_tasks')
        .select(`
          *,
          assignee:profiles!tender_tasks_assignee_id_fkey(
            id,
            email,
            company_name
          )
        `)
        .in('tender_id', tenders.map(t => t.id))
        .not('due_date', 'is', null);

      if (error) throw error;
      return data;
    },
    enabled: tenders.length > 0
  });

  // When tenders change, re-check the selected date to update what's visible
  useEffect(() => {
    if (date) {
      updateSelectedDateItems(date);
    }
  }, [tenders, tasks]);

  const handleTenderClick = (tender: any) => {
    navigate(`/offer-management/${tender.id}`);
  };

  const handleTaskClick = (task: any) => {
    navigate(`/offer-management/${task.tender_id}`);
  };

  // Helper to extract deadline from tender object, handling different structures
  const extractDeadlineDate = (tender: any): Date | null => {
    if (!tender) return null;
    
    // For tenders with tender_data
    if (tender.tender_data) {
      // Try multiple possible deadline paths
      const deadlineValues = [
        // Array paths first
        tender.tender_data.match_reasons?.["deadline-receipt-tender-date-lot"]?.[0],
        tender.tender_data["deadline-receipt-tender-date-lot"]?.[0],
        // Then direct paths
        tender.tender_data.deadline_date,
        tender.tender_data.deadline
      ];
      
      // Find first non-null value
      for (const deadlineValue of deadlineValues) {
        if (deadlineValue) {
          try {
            const parsedDate = safeParseDate(deadlineValue);
            if (parsedDate) {
              return parsedDate;
            }
          } catch (e) {
            console.error("Error parsing deadline:", e);
          }
        }
      }
    }
    
    // For direct deadline properties
    if (tender.deadline_date) {
      try {
        const parsedDate = safeParseDate(tender.deadline_date);
        if (parsedDate) {
          return parsedDate;
        }
      } catch (e) {
        console.error("Error parsing deadline:", e);
      }
    }
    
    if (tender.deadline) {
      try {
        const parsedDate = safeParseDate(tender.deadline);
        if (parsedDate) {
          return parsedDate;
        }
      } catch (e) {
        console.error("Error parsing deadline:", e);
      }
    }
    
    return null;
  };

  // Get dates with deadlines (both tenders and tasks)
  const getDatesWithDeadlines = () => {
    const dates: Date[] = [];
    
    // Add tender deadlines
    tenders.forEach(tender => {
      const deadlineDate = extractDeadlineDate(tender);
      if (deadlineDate) {
        dates.push(deadlineDate);
      }
    });

    // Add task deadlines
    tasks.forEach(task => {
      if (task.due_date) {
        try {
          const taskDate = new Date(task.due_date);
          if (!isNaN(taskDate.getTime())) {
            dates.push(taskDate);
          }
        } catch (e) {
          console.error("Error processing task date:", e);
        }
      }
    });

    return dates;
  };

  // Get task deadline dates
  const getTaskDeadlineDates = () => {
    return tasks.reduce((dates: Date[], task) => {
      if (task.due_date) {
        try {
          const taskDate = new Date(task.due_date);
          if (!isNaN(taskDate.getTime())) {
            dates.push(taskDate);
          }
        } catch (e) {
          console.error("Error processing task date:", e);
        }
      }
      return dates;
    }, []);
  };

  // Check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  // Update the selected date items based on current date
  const updateSelectedDateItems = (selectedDate: Date) => {
    // Filter tenders for the selected date
    const tendersForDate = tenders.filter(tender => {
      const deadlineDate = extractDeadlineDate(tender);
      return deadlineDate ? isSameDay(deadlineDate, selectedDate) : false;
    });

    // Filter tasks for the selected date
    const tasksForDate = tasks.filter(task => {
      if (!task.due_date) return false;
      try {
        const taskDate = new Date(task.due_date);
        return isSameDay(taskDate, selectedDate);
      } catch (e) {
        console.error("Error comparing task dates:", e);
        return false;
      }
    });

    setSelectedDateItems({
      tenders: tendersForDate,
      tasks: tasksForDate
    });
  };

  // Handle date selection
  const handleSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    if (selectedDate) {
      updateSelectedDateItems(selectedDate);
    } else {
      setSelectedDateItems({ tenders: [], tasks: [] });
    }
  };

  // Get the title from a tender object with line break for better display
  const getTenderTitle = (tender: any) => {
    if (tender.tender_data) {
      if (tender.tender_data.match_reasons?.["notice-title"]?.eng) {
        const title = tender.tender_data.match_reasons["notice-title"].eng;
        return formatTitleWithLineBreak(title);
      }
      
      if (tender.tender_data.match_reasons?.notice_title?.eng?.[0]) {
        const title = tender.tender_data.match_reasons.notice_title.eng[0];
        return formatTitleWithLineBreak(title);
      }
      
      if (tender.tender_data["notice-title"]?.eng) {
        const title = tender.tender_data["notice-title"].eng;
        return formatTitleWithLineBreak(title);
      }
      
      if (tender.tender_data.title) {
        return formatTitleWithLineBreak(tender.tender_data.title);
      }
    }
    
    if (tender.title) {
      return formatTitleWithLineBreak(tender.title);
    }
    
    return "Untitled Tender";
  };

  // Format title with line break for better display
  const formatTitleWithLineBreak = (title: string) => {
    if (!title || title.length <= 40) return title;
    
    const breakPoint = title.substring(30, 50).indexOf(' ');
    if (breakPoint >= 0) {
      const firstPart = title.substring(0, 30 + breakPoint);
      const secondPart = title.substring(30 + breakPoint + 1);
      return `${firstPart}\n${secondPart}`;
    }
    
    return title;
  };

  // Get organization name from tender data with priority to organisation-name-buyer
  const getOrganizationName = (tender: any) => {
    if (!tender || !tender.tender_data) return "Unknown Organization";

    const data = tender.tender_data;

    if (data["organisation-name-buyer"]) {
      const orgNameBuyer = data["organisation-name-buyer"];
      
      if (typeof orgNameBuyer === 'object' && orgNameBuyer !== null) {
        const langs = Object.keys(orgNameBuyer);
        if (langs.length > 0) {
          const orgValue = orgNameBuyer[langs[0]];
          if (Array.isArray(orgValue) && orgValue.length > 0) {
            return orgValue[0];
          } else if (typeof orgValue === 'string') {
            return orgValue;
          }
        }
      } else if (Array.isArray(orgNameBuyer) && orgNameBuyer.length > 0) {
        return orgNameBuyer[0];
      } else if (typeof orgNameBuyer === 'string') {
        return orgNameBuyer;
      }
    }
    
    if (data.organization_name) {
      return String(data.organization_name);
    }
    
    if (isObject(data.match_reasons)) {
      if (isObject(data.match_reasons["buyer-name"])) {
        if ("eng" in data.match_reasons["buyer-name"] && data.match_reasons["buyer-name"].eng !== undefined) {
          const engValue = data.match_reasons["buyer-name"].eng;
          if (Array.isArray(engValue) && engValue.length > 0) {
            return String(engValue[0]);
          } else if (typeof engValue === 'string') {
            return String(engValue);
          }
        }
        
        const langs = Object.keys(data.match_reasons["buyer-name"]);
        if (langs.length > 0) {
          const langValue = data.match_reasons["buyer-name"][langs[0]];
          if (Array.isArray(langValue) && langValue.length > 0) {
            return String(langValue[0]);
          } else if (typeof langValue === 'string') {
            return String(langValue);
          }
        }
      }
    }
    
    if (data["buyer-name"]) {
      const buyerNameObj = data["buyer-name"];
      
      if (typeof buyerNameObj === 'object' && buyerNameObj !== null) {
        if ("eng" in buyerNameObj && buyerNameObj.eng !== undefined) {
          const engValue = buyerNameObj.eng;
          if (Array.isArray(engValue) && engValue.length > 0) {
            return String(engValue[0]);
          } else if (typeof engValue === 'string') {
            return String(engValue);
          }
        }
        
        const langs = Object.keys(buyerNameObj);
        if (langs.length > 0) {
          const langValue = buyerNameObj[langs[0]];
          if (Array.isArray(langValue) && langValue.length > 0) {
            return String(langValue[0]);
          } else if (typeof langValue === 'string') {
            return String(langValue);
          }
        }
      } else if (typeof buyerNameObj === 'string') {
        return String(buyerNameObj);
      }
    }
    
    if (data["organisation-name-buyer"]) {
      const orgName = data["organisation-name-buyer"];
      if (typeof orgName === 'object' && !Array.isArray(orgName)) {
        if (orgName.eng) {
          if (Array.isArray(orgName.eng) && orgName.eng.length > 0) {
            return orgName.eng[0];
          }
          return orgName.eng;
        }
        const langs = Object.keys(orgName);
        if (langs.length > 0) {
          const firstLangValue = orgName[langs[0]];
          if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
            return firstLangValue[0];
          }
          if (firstLangValue) {
            return firstLangValue;
          }
        }
      }
    }
    
    if (data.AU !== undefined) {
      if (typeof data.AU === 'object' && data.AU !== null) {
        const langs = Object.keys(data.AU);
        if (langs.length > 0 && Array.isArray(data.AU[langs[0]])) {
          return String(data.AU[langs[0]][0] || "Unknown Organization");
        }
      }
      return String(data.AU);
    }
    
    return "Unknown Organization";
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-4">{t("dashboard.calendar.title", "Kalender")}</h2>
        <div className="flex justify-center w-full">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            className="w-full max-w-full rounded-md border pointer-events-auto"
            modifiers={{
              deadline: getDatesWithDeadlines(),
              taskDeadline: getTaskDeadlineDates(),
            }}
            modifiersStyles={{
              deadline: { 
                fontWeight: 'bold',
                backgroundColor: 'rgba(239, 68, 68, 0.1)', // red-500 with opacity
                color: 'rgb(239, 68, 68)' // red-500
              },
              taskDeadline: {
                fontWeight: 'bold',
                backgroundColor: 'rgba(59, 130, 246, 0.1)', // blue-500 with opacity
                color: 'rgb(59, 130, 246)' // blue-500
              }
            }}
            styles={{
              caption: { width: '100%' },
              month: { width: '100%' },
              table: { width: '100%' },
              head_row: { width: '100%', justifyContent: 'space-around' },
              row: { width: '100%', justifyContent: 'space-around' },
              day: { margin: '0', width: '34px', height: '34px' },
              nav: { margin: '0' }
            }}
          />
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-4">
          {date 
            ? `${t("dashboard.calendar.deadline", "Deadline")} ${formatDate(date.toISOString())}` 
            : t("dashboard.calendar.selectDate", "Vælg en dato")}
        </h2>
        <ScrollArea className="h-[300px] pr-3">
          {(selectedDateItems.tenders.length > 0 || selectedDateItems.tasks.length > 0) ? (
            <div className="space-y-5">
              {selectedDateItems.tenders.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-gray-500">{t("dashboard.calendar.tenderDeadlines", "Udbudsfrister")}</h3>
                  {selectedDateItems.tenders.map((tender, index) => {
                    const deadlineDate = extractDeadlineDate(tender);
                    return (
                      <Button
                        key={`tender-${index}`}
                        variant="outline"
                        className="w-full justify-between p-3 h-auto"
                        onClick={() => handleTenderClick(tender)}
                      >
                        <div className="flex-1 text-left overflow-hidden mr-2">
                          <h3 className="font-medium mb-1.5 break-words whitespace-pre-line" title={getTenderTitle(tender)}>
                            {getTenderTitle(tender)}
                          </h3>
                          <div className="text-sm text-gray-600 flex flex-col space-y-1">
                            <div className="flex items-start">
                              <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.organization", "Organisation")}:</span>
                              <span className="line-clamp-1" title={getOrganizationName(tender)}>
                                {getOrganizationName(tender)}
                              </span>
                            </div>
                            <div className="flex items-start">
                              <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.deadline", "Deadline")}:</span>
                              <span className="whitespace-nowrap">
                                {deadlineDate ? formatDate(deadlineDate.toISOString()) : t("dashboard.calendar.notSpecified", "Ikke angivet")}
                              </span>
                            </div>
                            <div className="flex items-start">
                              <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.value", "Værdi")}:</span>
                              <span className="whitespace-nowrap">
                                {tender.tender_data?.value ? `€${tender.tender_data.value}` : t("dashboard.calendar.notSpecified", "Ikke angivet")}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ExternalLink className="h-4 w-4 flex-shrink-0 ml-1" />
                      </Button>
                    );
                  })}
                </div>
              )}

              {selectedDateItems.tasks.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-gray-500">{t("dashboard.calendar.taskDeadlines", "Opgavefrister")}</h3>
                  {selectedDateItems.tasks.map((task, index) => (
                    <Button
                      key={`task-${index}`}
                      variant="outline"
                      className="w-full justify-between p-3 h-auto"
                      onClick={() => handleTaskClick(task)}
                    >
                      <div className="flex-1 text-left overflow-hidden mr-2">
                        <h3 className="font-medium mb-1.5 line-clamp-1" title={task.title}>{task.title}</h3>
                        <div className="text-sm text-gray-600 flex flex-col space-y-1">
                          <div className="flex items-start">
                            <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.assignedTo", "Tildelt til")}:</span>
                            <span className="line-clamp-1" title={task.assignee?.company_name || task.assignee?.email || t("dashboard.calendar.unassigned", "Ikke tildelt")}>
                              {task.assignee?.company_name || task.assignee?.email || t("dashboard.calendar.unassigned", "Ikke tildelt")}
                            </span>
                          </div>
                          <div className="flex items-start">
                            <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.dueDate", "Forfaldsdato")}:</span>
                            <span className="whitespace-nowrap">
                              {formatDate(task.due_date)}
                            </span>
                          </div>
                          <div className="flex items-start">
                            <span className="font-medium w-32 inline-block flex-shrink-0">{t("dashboard.calendar.priority", "Prioritet")}:</span>
                            <span className="whitespace-nowrap">
                              {task.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                      <ExternalLink className="h-4 w-4 flex-shrink-0 ml-1" />
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {date ? t("dashboard.calendar.noDeadlines", "Ingen deadlines på denne dato") : t("dashboard.calendar.selectDate", "Vælg en dato")}
            </div>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
};
